import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import TopNav from './TopNav';
import api from '../services/api';

interface QAResult {
  ruleId: string;
  passed: boolean;
  actual: any;
  expected: any;
  message: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  timestamp: Date;
}

interface QAMetrics {
  totalRules: number;
  passedRules: number;
  failedRules: number;
  criticalFailures: number;
  averagePassRate: number;
  lastSessionTime: string;
}

interface QAData {
  results: QAResult[];
  metrics: QAMetrics;
  metadata: {
    totalRules: number;
    passedRules: number;
    failedRules: number;
    criticalFailures: number;
    lastUpdated: string;
  };
}

const QAResults: React.FC = () => {
  const { } = useAuth();
  const [data, setData] = useState<QAData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Persist active tab to localStorage
  const getInitialTab = (): 'results' | 'metrics' | 'history' => {
    const savedTab = localStorage.getItem('qaResultsActiveTab') as 'results' | 'metrics' | 'history' | null;
    if (savedTab && ['results', 'metrics', 'history'].includes(savedTab)) {
      return savedTab;
    }
    return 'results';
  };
  
  const [activeTab, setActiveTab] = useState<'results' | 'metrics' | 'history'>(getInitialTab());
  
  const handleTabChange = (tab: 'results' | 'metrics' | 'history') => {
    setActiveTab(tab);
    localStorage.setItem('qaResultsActiveTab', tab);
  };
  const [filterBy, setFilterBy] = useState<'all' | 'passed' | 'failed' | 'critical'>('all');
  const [sortBy, setSortBy] = useState<'severity' | 'timestamp' | 'ruleId'>('severity');

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/selectors/qa-results');
      setData(response.data.data);
    } catch (err) {
      console.error('Failed to fetch QA results data:', err);
      setError('Failed to load QA results data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-400';
      case 'high': return 'text-orange-400';
      case 'medium': return 'text-yellow-400';
      case 'low': return 'text-green-400';
      default: return 'text-neutral-400';
    }
  };

  const getSeverityBgColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-900/30';
      case 'high': return 'bg-orange-900/30';
      case 'medium': return 'bg-yellow-900/30';
      case 'low': return 'bg-green-900/30';
      default: return 'bg-neutral-900/30';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return 'CRIT';
      case 'high': return 'HIGH';
      case 'medium': return 'MED';
      case 'low': return 'LOW';
      default: return 'UNK';
    }
  };

  const getPassRateColor = (rate: number) => {
    if (rate >= 90) return 'text-green-400';
    if (rate >= 70) return 'text-yellow-400';
    return 'text-red-400';
  };

  const filteredResults = data?.results.filter(result => {
    if (filterBy === 'all') return true;
    if (filterBy === 'passed') return result.passed;
    if (filterBy === 'failed') return !result.passed;
    if (filterBy === 'critical') return result.severity === 'critical' && !result.passed;
    return true;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'severity':
        const severityOrder = { 'critical': 1, 'high': 2, 'medium': 3, 'low': 4 };
        return severityOrder[a.severity] - severityOrder[b.severity];
      case 'timestamp':
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      case 'ruleId':
        return a.ruleId.localeCompare(b.ruleId);
      default:
        return 0;
    }
  }) || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950">
        <TopNav />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
            <span className="ml-3 text-neutral-400">Loading QA Results...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-950">
        <TopNav />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950">
      <TopNav />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <div className="mb-4">
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          </div>

          {/* Coming Soon Banner */}
          <div className="mb-6 bg-gradient-to-r from-cyan-900/40 to-blue-900/40 border border-cyan-500/50 rounded-lg p-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-cyan-300 mb-2">🚧 Feature In Development</h3>
                <p className="text-neutral-300 mb-2">
                  The QA Acceptance Check system is currently under development. While the policy rules and validation engine are configured, 
                  the integration with live trading sessions is not yet complete.
                </p>
                <p className="text-sm text-neutral-400">
                  <strong>What's Ready:</strong> 6 comprehensive validation rules covering allocation, swap limits, promotion quality, conviction gates, fast-track events, and sector quotas.
                  <br />
                  <strong>What's Next:</strong> Connecting the QA engine to the market screening pipeline for real-time validation.
                </p>
              </div>
            </div>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">QA Acceptance Check Results</h1>
            <p className="text-neutral-400">
              Automated quality assurance for Trading Universe • Last updated {data?.metadata.lastUpdated ? new Date(data.metadata.lastUpdated).toLocaleTimeString() : 'Unknown'}
            </p>
          </div>

          {/* Summary Cards */}
          {data && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-neutral-800 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-neutral-400">Total Rules</p>
                    <p className="text-2xl font-bold text-white">{data.metadata.totalRules}</p>
                  </div>
                  <div className="text-2xl font-bold text-cyan-400">RULES</div>
                </div>
              </div>
              
              <div className="bg-neutral-800 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-neutral-400">Passed</p>
                    <p className="text-2xl font-bold text-green-400">{data.metadata.passedRules}</p>
                  </div>
                  <div className="text-2xl font-bold text-green-400">PASS</div>
                </div>
              </div>
              
              <div className="bg-neutral-800 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-neutral-400">Failed</p>
                    <p className="text-2xl font-bold text-red-400">{data.metadata.failedRules}</p>
                  </div>
                  <div className="text-2xl font-bold text-red-400">FAIL</div>
                </div>
              </div>
              
              <div className="bg-neutral-800 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-neutral-400">Critical Issues</p>
                    <p className="text-2xl font-bold text-red-400">{data.metadata.criticalFailures}</p>
                  </div>
                  <div className="text-2xl font-bold text-red-400">ALERT</div>
                </div>
              </div>
            </div>
          )}

          {/* Pass Rate Indicator */}
          {data && (
            <div className="bg-neutral-800 rounded-lg p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Overall Pass Rate</h3>
                <span className={`text-2xl font-bold ${getPassRateColor((data.metadata.passedRules / data.metadata.totalRules) * 100)}`}>
                  {((data.metadata.passedRules / data.metadata.totalRules) * 100).toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-neutral-600 rounded-full h-3">
                <div 
                  className={`h-3 rounded-full ${getPassRateColor((data.metadata.passedRules / data.metadata.totalRules) * 100).replace('text-', 'bg-')}`}
                  style={{ width: `${(data.metadata.passedRules / data.metadata.totalRules) * 100}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="mb-6">
            <div className="flex space-x-4">
              <button
                onClick={() => handleTabChange('results')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'results'
                    ? 'bg-cyan-600 text-white'
                    : 'bg-neutral-800 text-neutral-400 hover:text-white'
                }`}
              >
                Current Results
              </button>
              <button
                onClick={() => handleTabChange('metrics')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'metrics'
                    ? 'bg-cyan-600 text-white'
                    : 'bg-neutral-800 text-neutral-400 hover:text-white'
                }`}
              >
                Metrics
              </button>
              <button
                onClick={() => handleTabChange('history')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'history'
                    ? 'bg-cyan-600 text-white'
                    : 'bg-neutral-800 text-neutral-400 hover:text-white'
                }`}
              >
                History
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'results' && (
            <div className="bg-neutral-800 rounded-lg p-6">
              {/* Controls */}
              <div className="flex flex-wrap gap-4 mb-6">
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Filter by</label>
                  <select
                    value={filterBy}
                    onChange={(e) => setFilterBy(e.target.value as any)}
                    className="bg-neutral-700 text-white px-3 py-2 rounded-lg border border-neutral-600"
                  >
                    <option value="all">All Results</option>
                    <option value="passed">Passed Only</option>
                    <option value="failed">Failed Only</option>
                    <option value="critical">Critical Issues</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Sort by</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="bg-neutral-700 text-white px-3 py-2 rounded-lg border border-neutral-600"
                  >
                    <option value="severity">Severity</option>
                    <option value="timestamp">Timestamp</option>
                    <option value="ruleId">Rule ID</option>
                  </select>
                </div>
              </div>

              {/* Results Table */}
              <div className="space-y-4">
                {filteredResults.map((result) => (
                  <div key={result.ruleId} className="bg-neutral-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{getSeverityIcon(result.severity)}</span>
                        <h4 className="text-lg font-semibold text-white">{result.ruleId}</h4>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          result.passed ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'
                        }`}>
                          {result.passed ? 'PASSED' : 'FAILED'}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSeverityBgColor(result.severity)} ${getSeverityColor(result.severity)}`}>
                          {result.severity.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-neutral-300 mb-3">{result.message}</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <h5 className="font-medium text-white mb-1">Expected:</h5>
                        <div className="bg-neutral-600 rounded p-2 text-neutral-300">
                          <pre className="whitespace-pre-wrap">{JSON.stringify(result.expected, null, 2)}</pre>
                        </div>
                      </div>
                      <div>
                        <h5 className="font-medium text-white mb-1">Actual:</h5>
                        <div className="bg-neutral-600 rounded p-2 text-neutral-300">
                          <pre className="whitespace-pre-wrap">{JSON.stringify(result.actual, null, 2)}</pre>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-3 text-xs text-neutral-400">
                      Timestamp: {new Date(result.timestamp).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'metrics' && data && (
            <div className="bg-neutral-800 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4">QA Metrics & Performance</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-neutral-700 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-white mb-2">Pass Rate Trend</h4>
                  <div className="text-3xl font-bold text-green-400 mb-2">
                    {((data.metadata.passedRules / data.metadata.totalRules) * 100).toFixed(1)}%
                  </div>
                  <p className="text-sm text-neutral-400">
                    {data.metadata.passedRules} of {data.metadata.totalRules} rules passed
                  </p>
                </div>
                
                <div className="bg-neutral-700 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-white mb-2">Critical Issues</h4>
                  <div className="text-3xl font-bold text-red-400 mb-2">
                    {data.metadata.criticalFailures}
                  </div>
                  <p className="text-sm text-neutral-400">
                    {data.metadata.criticalFailures === 0 ? 'No critical issues' : 'Critical issues detected'}
                  </p>
                </div>
                
                <div className="bg-neutral-700 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-white mb-2">Last Session</h4>
                  <div className="text-lg font-bold text-white mb-2">
                    {new Date(data.metadata.lastUpdated).toLocaleTimeString()}
                  </div>
                  <p className="text-sm text-neutral-400">
                    {new Date(data.metadata.lastUpdated).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="bg-neutral-800 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4">QA History</h3>
              <p className="text-neutral-400">
                Historical QA results and trends will be displayed here once implemented.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QAResults;
